import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import CharacterCount from '@tiptap/extension-character-count';
import Paragraph from '@tiptap/extension-paragraph';
import { Button } from '@/components/ui/button';
import { 
  Bold, 
  Italic, 
  Underline as UnderlineIcon, 
  Strikethrough, 
  Link as LinkIcon,
  List,
  ListOrdered,
  Heading3,
  Heading4,
  Type
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCallback, useEffect } from 'react';

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

export const RichTextEditor = ({ content, onChange, placeholder }: RichTextEditorProps) => {
  const handleSetLink = useCallback((editorInstance: any) => {
    if (!editorInstance) return;

    const previousUrl = editorInstance.getAttributes('link').href;
    const url = window.prompt('URL do link:', previousUrl);

    if (url === null) return;

    if (url === '') {
      editorInstance.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editorInstance.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [3, 4],
        },
        hardBreak: {
          keepMarks: true,
        },
        paragraph: false, // We'll use custom paragraph extension
      }),
      Paragraph.extend({
        parseHTML() {
          return [{ tag: 'p' }];
        },
        renderHTML({ HTMLAttributes }) {
          return ['p', HTMLAttributes, 0];
        },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener',
        },
      }),
      CharacterCount,
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[400px] px-3 py-2 break-words',
      },
      handleKeyDown: (view, event) => {
        // Ctrl+K or Cmd+K for link
        if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
          event.preventDefault();
          setTimeout(() => {
            if (editor) handleSetLink(editor);
          }, 0);
          return true;
        }
        return false;
      },
    },
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [editor, content]);

  if (!editor) {
    return null;
  }

  const ToolbarButton = ({ 
    onClick, 
    isActive, 
    icon: Icon, 
    title 
  }: { 
    onClick: () => void; 
    isActive?: boolean; 
    icon: any; 
    title: string 
  }) => (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClick}
      className={cn(
        "h-8 w-8 p-0",
        isActive && "bg-accent text-accent-foreground"
      )}
      title={title}
    >
      <Icon className="h-4 w-4" />
    </Button>
  );

  return (
    <div className="border border-input rounded-md bg-background overflow-hidden">
      <div className="border-b border-input p-2 flex flex-wrap gap-1">
        {/* Text Style Buttons */}
        <div className="flex gap-1 pr-2 border-r border-input">
          <ToolbarButton
            onClick={() => editor.chain().focus().setParagraph().run()}
            isActive={editor.isActive('paragraph')}
            icon={Type}
            title="Parágrafo"
          />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            isActive={editor.isActive('heading', { level: 3 })}
            icon={Heading3}
            title="Título (H3)"
          />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
            isActive={editor.isActive('heading', { level: 4 })}
            icon={Heading4}
            title="Subtítulo (H4)"
          />
        </div>

        {/* Text Formatting Buttons */}
        <div className="flex gap-1 pr-2 border-r border-input">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive('bold')}
            icon={Bold}
            title="Negrito (Ctrl+B)"
          />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive('italic')}
            icon={Italic}
            title="Itálico (Ctrl+I)"
          />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            isActive={editor.isActive('underline')}
            icon={UnderlineIcon}
            title="Sublinhado"
          />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleStrike().run()}
            isActive={editor.isActive('strike')}
            icon={Strikethrough}
            title="Tachado"
          />
        </div>

        {/* Link Button */}
        <div className="flex gap-1 pr-2 border-r border-input">
          <ToolbarButton
            onClick={() => handleSetLink(editor)}
            isActive={editor.isActive('link')}
            icon={LinkIcon}
            title="Inserir link (Ctrl+K)"
          />
        </div>

        {/* List Buttons */}
        <div className="flex gap-1">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive('bulletList')}
            icon={List}
            title="Lista com marcadores"
          />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive('orderedList')}
            icon={ListOrdered}
            title="Lista numerada"
          />
        </div>
      </div>

      <div className="overflow-x-hidden">
        <EditorContent editor={editor} />
      </div>

      <div className="border-t border-input p-2 text-xs text-muted-foreground">
        {editor.storage.characterCount.characters()} caracteres
      </div>
    </div>
  );
};
